"use client";

import React from "react";
import { Phone, ArrowRight, ShieldCheck, MapPin, Clock } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
export default function Quote() {
  const shouldReduceMotion = useReducedMotion();
  const trustPoints = [{
    title: "Regional coverage",
    description: "Berkshire, Surrey, Hampshire, Buckinghamshire, London & South East",
    icon: MapPin
  }, {
    title: "Verified safety",
    description: "NASC & CHAS accredited standard",
    icon: ShieldCheck
  }, {
    title: "Direct response",
    description: "Monday to Friday, 8am – 6pm with dedicated site contacts",
    icon: Clock
  }];
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1],
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="request-a-quote" data-nav="dark" className="dark bg-background text-foreground py-20 lg:py-28 relative overflow-hidden border-t border-border">
      {/* Background structural datum grid accents */}
      <div className="absolute inset-0 pointer-events-none opacity-5">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:4rem_4rem]" />
      </div>

      <div className="container mx-auto px-5 sm:px-8 max-w-7xl relative z-10">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="space-y-12">
          {/* Main Hero Card Container */}
          <Card className="bg-secondary text-secondary-foreground border-border/40 shadow-2xl relative overflow-hidden rounded-xl">
            {/* Top Amber Datum Accent Line */}
            <div className="h-1.5 w-full bg-primary" />

            <CardContent className="p-8 sm:p-12 lg:p-16">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
                {/* Text & Primary Actions */}
                <motion.div variants={itemVariants} className="lg:col-span-7 space-y-6">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-accent/20 border border-border/40 text-xs font-bold tracking-wider uppercase text-primary">
                    <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                    Rapid Regional Dispatch
                  </div>

                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default leading-tight">
                    Ready to scaffold your next project?
                  </h2>

                  <p className="text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
                    Tell us where you are working across Berkshire, Surrey, Hampshire, Buckinghamshire, London, or the wider South East. We survey your site, confirm the specification, and get your access in place on schedule.
                  </p>

                  {/* Action Trio */}
                  <div className="flex flex-wrap items-center gap-4 pt-2">
                    <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-semibold h-12 px-7 transition-[background-color,box-shadow]">
                      <Link to="/ContactRequestQuotePage">
                        Request a Quote
                        <ArrowRight className="size-4 ml-1" />
                      </Link>
                    </Button>

                    <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-accent/40 h-12 px-6 transition-[background-color,border-color]">
                      <a href="tel:01344929900">
                        <Phone className="size-4 mr-1 text-primary" />
                        Call Now
                      </a>
                    </Button>

                    <Button asChild variant="ghost" size="lg" className="text-foreground hover:bg-accent/30 hover:text-foreground h-12 px-6 border border-border/60 transition-[background-color,border-color]">
                      <Link to="/ContactRequestQuotePage">
                        Discuss a Project
                      </Link>
                    </Button>
                  </div>
                </motion.div>

                {/* Direct Phone & Operational Spec Panel */}
                <motion.div variants={itemVariants} className="lg:col-span-5">
                  <div className="rounded-lg bg-card/60 border border-border p-6 sm:p-8 space-y-6 backdrop-blur-xs">
                    <div className="space-y-1">
                      <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                        Direct Operational Desk
                      </span>
                      <h3 className="text-2xl font-bold text-card-foreground font-default">
                        01344 929900
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        quotes@bmfscaffolding.co.uk
                      </p>
                    </div>

                    <div className="border-t border-border/80 pt-5 space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Working Hours</span>
                        <span className="font-medium text-card-foreground">Mon – Fri, 8am – 6pm</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Lead Experience</span>
                        <span className="font-medium text-card-foreground">20+ Years On-Site</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Core Accreditations</span>
                        <span className="font-medium text-primary">NASC · CHAS</span>
                      </div>
                    </div>

                    <div className="pt-2">
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Serving commercial main contractors, solar installers, roofing teams, and residential developers across the South East.
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </CardContent>
          </Card>

          {/* Bottom Trust & Operational Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {trustPoints.map((point, index) => {
            const IconComponent = point.icon;
            return <motion.div key={point.title} data-index={index} variants={itemVariants} className="rounded-lg border border-border/80 bg-card/40 p-6 flex items-start gap-4 transition-[border-color,background-color] hover:border-primary/50">
                  <div className="p-3 rounded-md bg-accent/40 text-primary shrink-0 border border-border/50">
                    <IconComponent className="size-5" />
                  </div>
                  <div className="space-y-1">
                    <h4 className="text-base font-bold text-foreground font-default">
                      {point.title}
                    </h4>
                    <p className="text-sm text-muted-foreground leading-snug">
                      {point.description}
                    </p>
                  </div>
                </motion.div>;
          })}
          </div>
        </motion.div>
      </div>
    </section>;
}